/**
 * @module modules/pivot-table-sk
 * @description <h2><code>pivot-table-sk</code></h2>
 *
 * Displays a DataFrame that has been pivoted and contains summary values (as
 * opposed to a DataFrame that has been pivoted and contains summary traces).
 * These values are displayed in a table, as opposed to being displayed on in a
 * plot.
 *
 * The inputs required are a DataFrame and a pivot.Request, which has details on
 * how the input DataFrame was pivoted.
 *
 * @evt Emits a change event with the sort history encoded as a string when the
 *    user sorts on a column.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: all[name]
    });
}
_export(exports, {
    SortSelection: function() {
        return SortSelection;
    },
    SortHistory: function() {
        return SortHistory;
    },
    keyValuesFromTraceSet: function() {
        return keyValuesFromTraceSet;
    },
    PivotTableSk: function() {
        return PivotTableSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _query = require("../../../infra-sk/modules/query");
const _pivotutil = require("../pivotutil");
require("../../../infra-sk/modules/paramset-sk");
require("../../../elements-sk/modules/icons/sort-icon-sk");
require("../../../elements-sk/modules/icons/arrow-drop-down-icon-sk");
require("../../../elements-sk/modules/icons/arrow-drop-up-icon-sk");
const _paramtools = require("../paramtools");
const _const = require("../const/const");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let SortSelection = class SortSelection {
    toggleDirection() {
        if (this.dir === 'down') {
            this.dir = 'up';
        } else {
            this.dir = 'down';
        }
    }
    /** Returns a compareFunc that sorts based on the state of this SortSelection.
   */ buildCompare(traceset, keyValues) {
        const compare = (a, b)=>{
            let ret = 0;
            if (this.kind === 'keyValues') {
                const aString = keyValues[a][this.column];
                const bString = keyValues[b][this.column];
                if (aString < bString) {
                    ret = -1;
                } else if (bString < aString) {
                    ret = 1;
                } else {
                    return 0;
                }
            } else {
                ret = traceset[a][this.column] - traceset[b][this.column];
            }
            if (this.dir === 'down') {
                ret = -ret;
            }
            return ret;
        };
        return compare;
    }
    /** Encodes the SortSelection as a string. */ encode() {
        const encodedDir = this.dir === 'up' ? 'u' : 'd';
        const encodedKind = this.kind === 'keyValues' ? 'k' : 's';
        return `${encodedDir}${encodedKind}${this.column}`;
    }
    /** Decode an encoded SortSelection from a string encoded by
   * SortSelection.encode(). */ static decode(s) {
        const dir = s[0] === 'u' ? 'up' : 'down';
        const kind = s[1] === 'k' ? 'keyValues' : 'summaryValues';
        const column = +s.slice(2);
        return new SortSelection(column, kind, dir);
    }
    constructor(column, kind, dir){
        // The column to sort on, the value is interpreted differently based
        // on the value of this.sortKind:
        //
        // If this.kind === 'keyValues' then it is an index into the keyValues.
        // If this.Kind === 'summaryValues' then it is an index into the trace values.
        this.column = 0;
        this.kind = 'summaryValues';
        this.dir = 'up';
        this.column = column;
        this.kind = kind;
        this.dir = dir;
    }
};
let SortHistory = class SortHistory {
    /** Moves the selected column to the front of the list for sorting, and also
   * reverses its current direction.
   */ selectColumnToSortOn(column, kind) {
        // Remove the matching SortSelection from history.
        let removed = [];
        for(let i = 0; i < this.history.length; i++){
            if (column === this.history[i].column && kind === this.history[i].kind) {
                removed = this.history.splice(i, 1);
                break;
            }
        }
        // Toggle its direction.
        removed[0].toggleDirection();
        // Then add back to the beginning of the list.
        this.history.unshift(removed[0]);
    }
    /** Returns a compareFunc that sorts based on the state of all the
   *  SortSelections in history.
   */ buildCompare(traceset, keyValues) {
        const compares = this.history.map((sel)=>sel.buildCompare(traceset, keyValues));
        const compare = (a, b)=>{
            let ret = 0;
            // Call each compareFunc in `compares` until one of them produces a
            // non-zero result. If all calls return 0 then this compare function also
            // returns 0.
            compares.some((colCompare)=>{
                ret = colCompare(a, b);
                return ret;
            });
            return ret;
        };
        return compare;
    }
    /** Encodes the SortHistory as a string.
   *
   * The format is of all the serialized history members joined by
   * dashes.
   */ encode() {
        return this.history.map((sel)=>sel.encode()).join('-');
    }
    /** Decodes a string previously encoded via this.encode() and uses it to set
   * the history state. */ decode(s) {
        this.history = s.split('-').map((encodedSortSelection)=>SortSelection.decode(encodedSortSelection));
    }
    constructor(numGroupBy, numSummaryValues){
        /** Columns will be sorted by the first entry in history. If that yields a
   * tie, then the second entry in history will be used to break the tie, etc.
   */ this.history = [];
        for(let i = 0; i < numSummaryValues; i++){
            this.history.push(new SortSelection(i, 'summaryValues', 'up'));
        }
        for(let i = 0; i < numGroupBy; i++){
            this.history.push(new SortSelection(i, 'keyValues', 'up'));
        }
    }
};
function keyValuesFromTraceSet(traceset, req) {
    const ret = {};
    Object.keys(traceset).forEach((traceKey)=>{
        // Parse the key.
        const ps = (0, _paramtools.fromKey)(traceKey);
        // Store the values for each key in group_by order.
        ret[traceKey] = req.group_by.map((colName)=>ps[colName]);
    });
    return ret;
}
let PivotTableSk = class PivotTableSk extends _lit.LitElement {
    willUpdate(changedProperties) {
        if (changedProperties.has('req')) {
            if (this.req) {
                this.sortHistory = new SortHistory(this.req.group_by.length, this.req.summary.length);
                if (this._pendingHistory) {
                    this.sortHistory.decode(this._pendingHistory);
                    this._pendingHistory = null;
                }
            } else {
                this.sortHistory = null;
            }
        }
        if (changedProperties.has('df') || changedProperties.has('req')) {
            if (this.df && this.req) {
                this.keyValues = keyValuesFromTraceSet(this.df.traceset, this.req);
            } else {
                this.keyValues = {};
            }
        }
        // Rebuild compare if any usage of sortHistory or keyValues changed, but simpler to just rebuild if we have enough data.
        if (this.sortHistory && this.df && this.req) {
            this.compare = this.sortHistory.buildCompare(this.df.traceset, this.keyValues);
        } else {
            this.compare = null;
        }
    }
    createRenderRoot() {
        return this;
    }
    render() {
        if (this.shouldValidate()) {
            const invalidMessage = (0, _pivotutil.validateAsPivotTable)(this.req);
            if (invalidMessage) {
                return (0, _lit.html)`<h2>Cannot display: ${invalidMessage}</h2>`;
            }
            if (!this.df) {
                return (0, _lit.html)`<h2>Cannot display: Data is missing.</h2>`;
            }
            return (0, _lit.html)` ${this.queryDefinition()}
        <table>
          ${this.tableHeader()} ${this.tableRows()}
        </table>`;
        }
        return (0, _lit.html)``;
    }
    set(df, req, query, encodedHistory = '') {
        this.df = df;
        this.req = req;
        this.query = query;
        if (encodedHistory !== '') {
            this._pendingHistory = encodedHistory;
        }
    }
    shouldValidate() {
        return !this.hasAttribute('disable_validation');
    }
    queryDefinition() {
        return (0, _lit.html)` <div class="querydef">
      <div>
        <span class="title">Query</span>
        <paramset-sk .paramsets=${[
            (0, _query.toParamSet)(this.query)
        ]}></paramset-sk>
      </div>
      <div>
        <span class="title">Group by:</span>
        ${this.req.group_by.join(', ')}
      </div>
      <div>
        <span class="title">Operation:</span>
        ${_pivotutil.operationDescriptions[this.req.operation]}
      </div>
      <div>
        <span class="title">Summaries:</span>
        ${this.req.summary.map((op)=>_pivotutil.operationDescriptions[op]).join(', ')}
      </div>
    </div>`;
    }
    tableHeader() {
        return (0, _lit.html)` <tr>
      ${this.keyColumnHeaders()} ${this.summaryColumnHeaders()}
    </tr>`;
    }
    keyColumnHeaders() {
        return this.req.group_by.map((groupBy, index)=>(0, _lit.html)`<th>${this.sortArrow(index, 'keyValues')} ${groupBy}</th>`);
    }
    summaryColumnHeaders() {
        return this.req.summary.map((summaryOperation, index)=>(0, _lit.html)`<th>
          ${this.sortArrow(index, 'summaryValues')} ${_pivotutil.operationDescriptions[summaryOperation]}
        </th>`);
    }
    sortArrow(column, kind) {
        const firstSortSelection = this.sortHistory.history[0];
        if (firstSortSelection.kind === kind) {
            if (column === firstSortSelection.column) {
                if (firstSortSelection.dir === 'up') {
                    return (0, _lit.html)`<arrow-drop-up-icon-sk
            title="Change sort order to descending."
            @click=${()=>this.changeSort(column, kind)}></arrow-drop-up-icon-sk>`;
                }
                return (0, _lit.html)`<arrow-drop-down-icon-sk
          title="Change sort order to ascending."
          @click=${()=>this.changeSort(column, kind)}></arrow-drop-down-icon-sk>`;
            }
        }
        return (0, _lit.html)`<sort-icon-sk
      title="Sort this column."
      @click=${()=>this.changeSort(column, kind)}></sort-icon-sk>`;
    }
    changeSort(column, kind) {
        this.sortHistory.selectColumnToSortOn(column, kind);
        this.compare = this.sortHistory.buildCompare(this.df.traceset, this.keyValues);
        this.dispatchEvent(new CustomEvent('change', {
            detail: this.sortHistory.encode(),
            bubbles: true
        }));
    }
    tableRows() {
        const traceset = this.df.traceset;
        const sortedRowKeys = Object.keys(traceset).sort(this.compare);
        const ret = [];
        sortedRowKeys.forEach((key)=>{
            ret.push((0, _lit.html)`<tr>
          ${this.keyRowValues(key)}${this.summaryRowValues(key)}
        </tr>`);
        });
        return ret;
    }
    keyRowValues(traceKey) {
        return this.keyValues[traceKey].map((value)=>(0, _lit.html)`<th class="key">${value}</th>`);
    }
    summaryRowValues(key) {
        return this.df.traceset[key].map((value)=>(0, _lit.html)`<td>${PivotTableSk.displayValue(value)}</td>`);
    }
    /** Converts vec32.MissingDataSentinel values into '-'. */ static displayValue(value) {
        // TODO(jcgregorio) Have a common definition of vec32.MissingDataSentinel in
        // TS and Go code.
        if (value === _const.MISSING_DATA_SENTINEL) {
            return '-';
        }
        return value.toPrecision(4);
    }
    constructor(...args){
        super(...args);
        this.df = null;
        this.req = null;
        this.query = '';
        /** Maps each traceKey to a list of the values for each key in the traceID,
   * where the order is determined by this.req.group_by.
   *
   * That is ',arch=arm,config=8888,' maps to ['8888', 'arm'] if
   * this.req.group_by is ['config', 'arch'].
   *  */ this.keyValues = {};
        this.sortHistory = null;
        // The comparison function to use to sort the table.
        this.compare = null;
        this._pendingHistory = null;
    }
};
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Object)
], PivotTableSk.prototype, "df", void 0);
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Object)
], PivotTableSk.prototype, "req", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], PivotTableSk.prototype, "query", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", typeof KeyValues === "undefined" ? Object : KeyValues)
], PivotTableSk.prototype, "keyValues", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Object)
], PivotTableSk.prototype, "sortHistory", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Object)
], PivotTableSk.prototype, "compare", void 0);
PivotTableSk = _ts_decorate([
    (0, _decorators.customElement)('pivot-table-sk')
], PivotTableSk);
